import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './Components/Navbar';
import Main from './Components/Main';
import ErrorBoundary from './Components/common/ErrorBoundary';
import LoadingScreen from './Components/common/LoadingScreen';
import { validateEnv } from './utils/validation';
import { ANIMATION_CONFIG } from './utils/constants';
import { debounce, prefersReducedMotion } from './utils/helpers';
import { ArrowUp } from 'lucide-react';

import HomePage from './pages/HomePage';

const Footer = lazy(() => import('./Components/Footer'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const TNHubPage = lazy(() => import('./pages/TNHubPage'));
const WorkPage = lazy(() => import('./pages/WorkPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));

// Accessible Loading Fallback
const LoadingFallback = () => (
  <div
    role="status"
    aria-live="polite"
    aria-busy="true"
    style={{
      display: 'flex',
      justifyContent: 'center',
      padding: '50px',
      color: 'var(--primary-color, #00f0ff)'
    }}
  >
    <span className="sr-only">Loading content...</span>
    <span aria-hidden="true">Loading Section...</span>
  </div>
);

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Handle loading screen completion
  const handleLoadingComplete = React.useCallback(() => {
    setIsLoading(false);
    // Focus on main content after loading for screen readers
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus({ preventScroll: true });
    }
  }, []);

  // Startup Validation
  useEffect(() => {
    validateEnv();
  }, []);

  // Optimized Scroll Spy & Button Visibility Logic
  useEffect(() => {
    const handleScroll = debounce(() => {
      const scrollY = window.scrollY;
      const shouldShow = scrollY > ANIMATION_CONFIG.scrollThreshold;

      // Batch updates
      if (showScrollTop !== shouldShow) {
        setShowScrollTop(shouldShow);
        setShowWhatsApp(shouldShow);
      }
    }, 100);

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const sections = document.querySelectorAll('section');
    sections.forEach((section) => observer.observe(section));

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      sections.forEach((section) => observer.unobserve(section));
      observer.disconnect();
    };
  }, [showScrollTop, location.pathname]); // re-run on path change

  const scrollToTop = () => {
    const shouldReduceMotion = prefersReducedMotion();
    window.scrollTo({
      top: 0,
      behavior: shouldReduceMotion ? 'auto' : 'smooth'
    });
    // Announce to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = 'Scrolled to top of page';
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  };

  return (
    <ErrorBoundary>
      {/* Skip to Content Link - Accessibility */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>

      {isLoading && <LoadingScreen onLoadingComplete={handleLoadingComplete} />}

      <Main>
        <Navbar activeSection={activeSection} />

        <main id="main-content" tabIndex="-1" role="main">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services/:serviceType" element={<ServicePage />} />
              <Route path="/web-developer-tamil-nadu" element={<TNHubPage />} />
              <Route path="/work" element={<WorkPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="*" element={<HomePage />} />
            </Routes>
            <Footer />
          </Suspense>
        </main>

        {/* Scroll Top Button */}
        <button
          className={`scroll-top ${showScrollTop ? 'visible' : ''}`}
          onClick={scrollToTop}
          aria-label="Scroll to top of page"
          title="Scroll to top"
        >
          <ArrowUp size={20} aria-hidden="true" />
        </button>

        {/* WhatsApp Float */}
        <a
          href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_PHONE || '8870020639'}?text=Hello%20Mowli!`}
          className={`whatsapp-float ${showWhatsApp ? 'visible' : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat with Mowli on WhatsApp (opens in new window)"
          title="Chat on WhatsApp"
        >
          <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
          </svg>
        </a>
      </Main>
    </ErrorBoundary>
  );
};

export default App;